import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

import { cn } from '../lib/cn';
import { Spinner } from './Spinner';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'danger'
  | 'link';
export type ButtonSize = 'sm' | 'md' | 'lg' | 'icon';

const base =
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded font-medium ' +
  'transition-colors disabled:pointer-events-none disabled:opacity-50 ' +
  'focus-visible:outline-2 focus-visible:outline-offset-2';

const variants: Record<ButtonVariant, string> = {
  primary: 'bg-primary text-primary-ink hover:bg-primary-hover shadow-sm',
  secondary:
    'bg-surface text-ink border border-line-strong hover:bg-surface-sunken shadow-sm',
  ghost: 'text-ink-muted hover:bg-surface-sunken hover:text-ink',
  // Reserved for actions that destroy or take money. Not for "cancel".
  danger: 'bg-danger text-white hover:opacity-90 shadow-sm',
  link: 'text-accent underline-offset-4 hover:underline',
};

const sizes: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-sm',
  md: 'h-9 px-4 text-base',
  lg: 'h-11 px-6 text-lg',
  icon: 'h-9 w-9 p-0',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Shows a spinner and disables the button. Use for in-flight mutations. */
  loading?: boolean;
  /** Announced while `loading`. Defaults to the button's own text. */
  loadingLabel?: string;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
  fullWidth?: boolean;
}

/**
 * The one button.
 *
 * A note that matters more here than in most products: a button rendered from a
 * permission check is a convenience, never a control. Hiding a button does not
 * protect anything — the server re-checks on execution. Render from `_actions` and
 * `available-transitions` because it is kinder, not because it is safer.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    loading = false,
    loadingLabel,
    leadingIcon,
    trailingIcon,
    fullWidth = false,
    className,
    children,
    disabled,
    type = 'button',
    ...props
  },
  ref,
) {
  return (
    <button
      ref={ref}
      // Defaulting to "button" rather than "submit" — an unlabelled button inside a
      // form submitting it by accident is a real and expensive class of bug here.
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(base, variants[variant], sizes[size], fullWidth && 'w-full', className)}
      {...props}
    >
      {loading ? (
        <Spinner size="sm" aria-hidden="true" />
      ) : (
        leadingIcon && <span aria-hidden="true">{leadingIcon}</span>
      )}
      {/* An icon button's icon may come as `leadingIcon` or as its children (it was
          dropped silently before, leaving an invisible button). Name it with aria-label. */}
      {(size !== 'icon' || (!leadingIcon && !loading)) && children}
      {loading && loadingLabel && <span className="sr-only">{loadingLabel}</span>}
      {!loading && trailingIcon && <span aria-hidden="true">{trailingIcon}</span>}
    </button>
  );
});
